// The only logging path (FR-041; US6-3; research R15; constitution VI). Outside production it writes
// a scrubbed line to the console; in production nothing is written or sent anywhere (no crash
// reporting service in this feature).
import { type AppEnv, ConfigError, readPublicConfig } from "@/config";

const REDACTED = "[redacted]";

// Order matters: tokens and JWTs before the looser number patterns.
const PATTERNS: readonly RegExp[] = [
  /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi,
  /\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
  /\b(token|code)=[^&\s#"']+/gi,
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
  /-?\d{1,3}\.\d{3,}\s*,\s*-?\d{1,3}\.\d{3,}/g,
  /(?:\+?91[\s-]?)?[6-9]\d{4}[\s-]?\d{5}\b/g,
];

/** Removes email addresses, phone numbers, bearer tokens, JWTs, token=/code= values and coordinates. */
export function scrub(text: string): string {
  return PATTERNS.reduce(
    (out, pattern) =>
      out.replace(pattern, (match, key?: string) =>
        typeof key === "string" && /^(token|code)$/i.test(key)
          ? `${key}=${REDACTED}`
          : REDACTED,
      ),
    text,
  );
}

function describe(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

function currentEnv(): AppEnv {
  try {
    return readPublicConfig().appEnv;
  } catch (error) {
    // Without a valid configuration, treat the build as production: write nothing.
    if (error instanceof ConfigError) return "production";
    throw error;
  }
}

type Options = {
  appEnv?: AppEnv;
  log?: (message: string) => void;
};

export function reportError(error: unknown, options: Options = {}): void {
  const appEnv = options.appEnv ?? currentEnv();
  if (appEnv === "production") return;
  const log = options.log ?? ((message: string) => console.error(message));
  log(`[akaiva] ${scrub(describe(error))}`);
}
